import { useEffect, useRef, useState } from "react";
import Badge from "@mui/material/Badge";
import { FiSearch, FiX } from "react-icons/fi";
import { RiShoppingBagLine } from "react-icons/ri";
import { useSelector } from "react-redux";
import { Link, useLocation } from "react-router-dom";
import type { RootState } from "../../store/store.ts";
import "./Navbar.css";

const NAV_LINKS = [
  { label: "Ready to Wear", to: "/shop?category=Ready%20To%20Wear" },
  { label: "New Arrival", to: "/shop" },
  { label: "Winter Collection", to: "/shop" },
  { label: "Best Selling", to: { pathname: "/", hash: "best" } },
  { label: "Shop All", to: "/shop" },
];

const Navbar = () => {
  const cart = useSelector((state: RootState) => state.cart.items);
  const location = useLocation();
  const searchInputRef = useRef<HTMLInputElement>(null);
  const openSearchButtonRef = useRef<HTMLButtonElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);
  const [searchModalOpen, setSearchModalOpen] = useState(false);

  const openSearchModal = () => setSearchModalOpen(true);
  const closeSearchModal = () => {
    setSearchModalOpen(false);
    openSearchButtonRef.current?.focus();
  };

  useEffect(() => {
    if (searchModalOpen) {
      searchInputRef.current?.focus();
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
  }, [searchModalOpen]);

  const isActive = (label: string) => {
    if (label === "Shop All") return location.pathname === "/shop" && !location.search;
    if (label === "Ready to Wear") {
      return location.search.toLowerCase().includes("ready");
    }
    if (label === "Best Selling") return location.hash === "#best";
    return false;
  };

  return (
    <>
      <header className="siteHeader">
        <div className="siteBar">
          Hurry — shop new before your favorite designs sell out!
        </div>
        <div className="siteHeaderMain">
          <Link to="/" className="siteWordmark" aria-label="Ayzen home">
            <img src="/assets/logo.png" alt="Ayzen" />
          </Link>
          <div className="siteTools">
            <button
              type="button"
              className="icon-button"
              onClick={searchModalOpen ? closeSearchModal : openSearchModal}
              aria-label={searchModalOpen ? "Close search" : "Open search"}
              ref={openSearchButtonRef}
            >
              {searchModalOpen ? <FiX size={18} /> : <FiSearch size={18} />}
            </button>
            <Link to="/cart" className="bagLink" aria-label="Shopping bag">
              <Badge
                badgeContent={cart.length || "0"}
                color="primary"
                sx={{
                  "& .MuiBadge-badge": {
                    background: "#1a1a1a",
                    color: "#fff",
                    fontSize: 10,
                    minWidth: 16,
                    height: 16,
                  },
                }}
              >
                <RiShoppingBagLine size={18} />
              </Badge>
            </Link>
          </div>
          <nav className="siteNav" aria-label="Primary">
            {NAV_LINKS.map((item) => (
              <Link
                key={item.label}
                to={item.to}
                className={isActive(item.label) ? "on" : undefined}
              >
                {item.label}
              </Link>
            ))}
          </nav>
        </div>
      </header>
      {searchModalOpen && (
        <div
          role="dialog"
          aria-modal
          className="SearchModel active"
          aria-labelledby="search-modal-title"
          ref={modalRef}
          onKeyDown={(event) => {
            if (event.key === "Escape") closeSearchModal();
          }}
        >
          <div className="searchHeader">
            <h4 id="search-modal-title">What are you looking for?</h4>
            <input
              type="text"
              placeholder="Search products"
              aria-label="Search products"
              ref={searchInputRef}
            />
          </div>
          <div className="searchQuickLinks">
            <h5 className="quick-links-title">QUICK LINKS</h5>
            <ul className="quick-links-list">
              <li>
                <Link
                  to="/shop?category=Ready%20To%20Wear"
                  className="quick-link"
                  onClick={closeSearchModal}
                >
                  Ready To Wear
                </Link>
              </li>
              <li>
                <Link
                  to="/shop?category=Unstitched"
                  className="quick-link"
                  onClick={closeSearchModal}
                >
                  Unstitched
                </Link>
              </li>
              <li>
                <Link
                  to="/shop?category=Formal%20Wear"
                  className="quick-link"
                  onClick={closeSearchModal}
                >
                  Formal Wear
                </Link>
              </li>
            </ul>
          </div>
        </div>
      )}
    </>
  );
};

export default Navbar;
